import React from 'react';
import { questionsData } from '../../data/questions';
import { calcDomainScore } from '../../utils/scoring';

const Alignment = ({ setPage, scores, setScores }) => {
  const handleAnswer = (questionId, value) => {
    setScores(prev => ({ ...prev, [questionId]: value }));
  };

  const printScore = calcDomainScore(scores, 'P');
  const itScore = calcDomainScore(scores, 'I');
  const cyberScore = calcDomainScore(scores, 'C');

  const printAnswered = Object.keys(scores).filter(k => k.startsWith('P')).length;
  const itAnswered = Object.keys(scores).filter(k => k.startsWith('I')).length;
  const cyberAnswered = Object.keys(scores).filter(k => k.startsWith('C')).length;

  const getInsights = () => {
    const insights = [];
    if (scores.P1 && scores.P1 < 3) insights.push({ icon: '🖨️', text: 'Limited print visibility → likely overspending on devices and supplies' });
    if (scores.P2 && scores.P2 < 3) insights.push({ icon: '⚠️', text: 'Reactive print support → higher downtime and emergency costs' });
    if (scores.P4 && scores.P4 < 3) insights.push({ icon: '📄', text: 'Print security gaps → sensitive document exposure risk' });
    if (scores.I1 && scores.I1 < 3) insights.push({ icon: '💻', text: 'Unmanaged endpoints → security blind spots and slow support' });
    if (scores.I2 && scores.I2 < 3) insights.push({ icon: '☁️', text: 'Limited cloud adoption → scalability and reliability concerns' });
    if (scores.I3 && scores.I3 < 3) insights.push({ icon: '💾', text: 'Backup gaps → business continuity risk' });
    if (scores.I5 && scores.I5 < 3) insights.push({ icon: '🔧', text: 'Reactive IT model → higher costs and constant firefighting' });
    if (scores.C1 && scores.C1 < 3) insights.push({ icon: '🛡️', text: 'Basic endpoint protection → vulnerable to modern threats' });
    if (scores.C3 && scores.C3 < 3) insights.push({ icon: '🔐', text: 'Missing MFA → HIGH account compromise risk' });
    if (scores.C2 && scores.C2 < 3) insights.push({ icon: '👥', text: 'Limited security training → phishing vulnerability' });
    if (scores.C5 && scores.C5 < 3) insights.push({ icon: '🚨', text: 'No IR plan → slower breach response, higher damage' });
    return insights;
  };

  const currentInsights = getInsights();

  const renderDomain = (domainKey, title, icon, colorClass, score) => (
    <div className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden border border-gray-200">
      <div className={`p-5 text-white flex items-center gap-3 ${colorClass}`}>
        <span className="text-2xl">{icon}</span>
        <span className="text-lg font-semibold">{title}</span>
        <span className="ml-auto bg-white/20 px-4 py-1.5 rounded-full font-bold">{score}/5</span>
      </div>
      <div className="p-6">
        {questionsData[domainKey].map((q, idx) => (
          <div key={q.id} className={`bg-[#F4F6F9] rounded-xl p-5 mb-5 border-l-4 transition-all hover:shadow-md ${scores[q.id] ? 'border-[#28A745] bg-gradient-to-br from-[#f0fff4] to-[#F4F6F9]' : 'border-[#2E5FA7]'}`}>
            <div className="flex justify-between items-start mb-3 gap-3">
              <div className="font-semibold text-gray-800 flex-1">{idx + 1}. {q.title}</div>
            </div>
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">{q.question}</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2.5 mb-4">
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(q.id, opt.value)}
                  className={`p-3 border-2 rounded-xl text-left transition-all text-xs flex flex-col justify-center
                    ${scores[q.id] === opt.value 
                      ? 'border-[#2E5FA7] bg-[#2E5FA7] text-white' 
                      : 'border-gray-200 bg-white hover:border-[#2E5FA7] hover:bg-[#2E5FA7]/5'}`}
                >
                  <span className="font-semibold block mb-1">{opt.label}</span>
                  <span className={`text-[10px] ${scores[q.id] === opt.value ? 'opacity-90' : 'opacity-70'}`}>{opt.desc}</span>
                </button>
              ))}
            </div>
            
            <div className="bg-white rounded-lg p-3.5 mt-3 border-l-4 border-[#0D6EFD]">
              <div className="font-semibold text-xs text-[#0D6EFD] mb-1 uppercase tracking-wide">Business Impact</div>
              <div className="text-xs text-gray-600 space-y-1">
                {q.impact.map((imp, i) => <div key={i}>• {imp}</div>)}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#fff9e6] to-white rounded-lg p-3.5 mt-2 border-l-4 border-[#FFC107]">
              <div className="font-semibold text-xs text-[#856404] mb-1 uppercase tracking-wide">💬 Ask This</div>
              <div className="text-xs text-gray-700 italic">"{q.followup}"</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in">
      <div className="bg-white rounded-2xl p-5 shadow-sm mb-6">
        <div className="flex justify-center gap-2 mb-6">
          <div className={`w-3 h-3 rounded-full transition-all ${printAnswered === 5 ? 'bg-[#28A745]' : printAnswered > 0 ? 'bg-[#2E5FA7] scale-125' : 'bg-gray-300'}`}></div>
          <div className={`w-3 h-3 rounded-full transition-all ${itAnswered === 5 ? 'bg-[#28A745]' : itAnswered > 0 ? 'bg-[#2E5FA7] scale-125' : 'bg-gray-300'}`}></div>
          <div className={`w-3 h-3 rounded-full transition-all ${cyberAnswered === 5 ? 'bg-[#28A745]' : cyberAnswered > 0 ? 'bg-[#2E5FA7] scale-125' : 'bg-gray-300'}`}></div>
        </div>
        
        <div className="bg-gradient-to-br from-[#244C86] to-[#2E5FA7] rounded-xl p-5 text-white">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">🔍 What We're Seeing</div>
          <div className="flex flex-col gap-2">
            {currentInsights.length === 0 ? (
              <div className="bg-white/15 p-2.5 rounded-lg text-sm flex items-center gap-2.5 opacity-60">
                <span>💡</span>
                <span>Complete the assessment to see real-time insights...</span>
              </div>
            ) : (
              currentInsights.slice(0, 5).map((insight, idx) => (
                <div key={idx} className="bg-white/15 p-2.5 rounded-lg text-sm flex items-center gap-2.5">
                  <span>{insight.icon}</span>
                  <span>{insight.text}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {renderDomain('print', 'Print & Document Environment', '🖨️', 'bg-gradient-to-br from-[#2E5FA7] to-[#244C86]', printScore)}
      {renderDomain('it', 'IT Infrastructure & Support', '💻', 'bg-gradient-to-br from-[#1a5f7a] to-[#134b5f]', itScore)}
      {renderDomain('cyber', 'Security & Compliance', '🔒', 'bg-gradient-to-br from-[#6b2d5b] to-[#4a1d3d]', cyberScore)}

      <div className="flex justify-between mt-8 pb-8">
        <button onClick={() => setPage('context')} className="px-6 py-3 rounded-lg font-semibold text-[#2E5FA7] border-2 border-[#2E5FA7] hover:bg-gray-50 transition-all">← Back</button>
        <button onClick={() => setPage('insights')} className="px-6 py-3 rounded-lg font-semibold bg-[#2E5FA7] text-white hover:bg-[#244C86] transition-all shadow-md">View Results →</button>
      </div>
    </div>
  );
};

export default Alignment;